(function () {
  "use strict";

  // ----------------- CONFIG -----------------
  const DL = (window.dataLayer = window.dataLayer || []);
  const YDL = (window.ymDataLayer = window.ymDataLayer || []);
  const CURRENCY = "KZT";

  // If true, Yandex ecommerce pushes will ALSO be duplicated into window.dataLayer.
  // Useful if Metrica reads dataLayer (default in many setups).
  const PUSH_YANDEX_TO_DATALAYER = true;

  // If you later want to use SKU as GA4 item_id, switch to true,
  // BUT make sure all places (list, click, product, cart) use the same id.
  const USE_SKU_AS_ITEM_ID = false;

  // ----------------- HELPERS -----------------
  function toFloat(v) {
    const n = typeof v === "string" ? parseFloat(v) : typeof v === "number" ? v : NaN;
    return Number.isFinite(n) ? n : null;
  }

  function safeJsonParse(s) {
    try {
      return JSON.parse(s);
    } catch (e) {
      return null;
    }
  }

  function getUrl(input) {
    if (typeof input === "string") return input;
    if (input && typeof input.url === "string") return input.url; // Request object
    return "";
  }

  function gaPush(eventName, ecommerceObj) {
    const eo = ecommerceObj && typeof ecommerceObj === "object" ? ecommerceObj : {};

    // Always keep currency consistent across ecommerce events
    if (!eo.currency) eo.currency = CURRENCY;

    // GA4 recommendation: clear previous ecommerce object
    DL.push({ ecommerce: null });
    DL.push({ event: eventName, ecommerce: eo });
  }
  function gaPushWithCallback(eventName, ecommerceObj, cb) {
    const eo = ecommerceObj && typeof ecommerceObj === "object" ? ecommerceObj : {};

    if (!eo.currency) eo.currency = CURRENCY;

    DL.push({ ecommerce: null });

    DL.push({
      event: eventName,
      ecommerce: eo,
      eventCallback: function () {
        try { cb && cb(); } catch (e) {}
      },
      eventTimeout: 800
    });
  }

  function yPush(obj) {
    YDL.push(obj);
    if (PUSH_YANDEX_TO_DATALAYER && YDL !== DL) DL.push(obj);
  }

  function yEcom(payload) {
    yPush({
      ecommerce: {
        currencyCode: CURRENCY,
        ...payload,
      },
    });
  }

  // ---------- Dedupe helper ----------
  const _dedupe = new Map();
  function once(key, ttlMs) {
    const now = Date.now();
    const last = _dedupe.get(key) || 0;
    if (now - last < (ttlMs || 1500)) return false;
    _dedupe.set(key, now);

    // Keep map small
    if (_dedupe.size > 300) {
      const cutoff = now - 15000;
      for (const [k, t] of _dedupe) {
        if (t < cutoff) _dedupe.delete(k);
      }
    }
    return true;
  }

  // ---------- Item ID strategy (keep consistent everywhere) ----------
  function getItemId(obj) {
    if (!obj) return "";

    // If you enable SKU, make sure list/click/product/cart all provide sku.
    if (USE_SKU_AS_ITEM_ID) {
      const sku = obj.sku || obj.variant_sku || obj.item_sku;
      if (sku) return String(sku);
    }

    // Prefer variant_id / id
    const id =
      obj.variant_id ||
      obj.id ||
      obj.item_id ||
      obj.variantId ||
      obj.variantID ||
      obj.product_variant_id;
    return id != null ? String(id) : "";
  }

  // ---------- List context storage (propagate item_list_name/index дальше) ----------
  function saveListCtx(item) {
    try {
      const key = `ec_list:${item.item_id}`;
      sessionStorage.setItem(
        key,
        JSON.stringify({
          item_list_name: item.item_list_name || "",
          index: item.index || null,
        })
      );
    } catch (e) {}
  }

  function applyListCtx(item) {
    try {
      const raw = sessionStorage.getItem(`ec_list:${item.item_id}`);
      if (!raw) return item;

      const ctx = safeJsonParse(raw);
      if (!ctx) return item;

      if (!item.item_list_name && ctx.item_list_name) item.item_list_name = ctx.item_list_name;
      if (!item.index && ctx.index) item.index = ctx.index;

      return item;
    } catch (e) {
      return item;
    }
  }

  // ---------- Cart helpers ----------
  function getUnitPriceFromCartLine(it) {
    // Try to get per-unit discounted price in cents, then convert to float currency.
    // Shopify cart.js often has: price (unit), final_price (unit discounted), line_price/final_line_price (line totals).
    const qty = it && it.quantity ? Number(it.quantity) : 1;

    let cents = null;

    if (it && typeof it.final_line_price === "number" && qty > 0) {
      cents = it.final_line_price / qty;
    } else if (it && typeof it.line_price === "number" && qty > 0) {
      cents = it.line_price / qty;
    } else if (it && typeof it.final_price === "number") {
      cents = it.final_price;
    } else if (it && typeof it.price === "number") {
      cents = it.price;
    }

    return cents != null ? toFloat(cents / 100) : null;
  }

  function buildGAItemFromCartLine(it, idx) {
    const item = {
      item_id: getItemId({ variant_id: it.variant_id, sku: it.sku }),
      item_name: it.product_title || it.title || "",
      item_brand: it.vendor || "",
      item_category: it.product_type || "",
      item_variant: it.variant_title || "",
      price: getUnitPriceFromCartLine(it) || undefined,
      quantity: it.quantity || 1,
      index: idx != null ? idx : undefined,
    };

    return applyListCtx(item);
  }

  async function fetchCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    return await res.json();
  }

  function indexCartByVariant(cart) {
    const map = new Map();
    const items = (cart && cart.items) || [];
    for (const it of items) {
      map.set(String(it.variant_id), it);
    }
    return map;
  }

  function diffCart(oldCart, newCart) {
    const oldMap = indexCartByVariant(oldCart || { items: [] });
    const newMap = indexCartByVariant(newCart || { items: [] });

    const variants = new Set([...oldMap.keys(), ...newMap.keys()]);
    const changes = [];

    for (const vid of variants) {
      const oldIt = oldMap.get(vid);
      const newIt = newMap.get(vid);

      const oldQty = oldIt ? Number(oldIt.quantity || 0) : 0;
      const newQty = newIt ? Number(newIt.quantity || 0) : 0;

      const delta = newQty - oldQty;
      if (!delta) continue;

      // Choose line data for price/name: prefer newIt, else oldIt
      const ref = newIt || oldIt;

      changes.push({
        variant_id: String(ref.variant_id),
        sku: ref.sku,
        product_title: ref.product_title || ref.title,
        title: ref.title,
        vendor: ref.vendor,
        product_type: ref.product_type,
        variant_title: ref.variant_title,
        unit_price: getUnitPriceFromCartLine(ref),
        delta_qty: delta,
      });
    }

    return changes;
  }

  // ----------------- EVENT HANDLERS -----------------

  // ---------- 1) view_item (Product page) ----------
  if (window.__ECOM_PRODUCT__ && window.__ECOM_VARIANT__) {
    const p = window.__ECOM_PRODUCT__;
    const v = window.__ECOM_VARIANT__;

    const item = applyListCtx({
      item_id: getItemId({ id: v.id, sku: v.sku }),
      item_name: p.title || "",
      item_brand: p.vendor || "",
      item_category: p.type || p.product_type || "",
      item_variant: v.title || "",
      price: toFloat((v.price || 0) / 100) || undefined,
      quantity: 1,
    });

    const value = item.price ? item.price * (item.quantity || 1) : undefined;

    gaPush("view_item", {
      value,
      items: [item],
    });

    yEcom({
      detail: {
        products: [
          {
            id: item.item_id,
            name: item.item_name,
            brand: item.item_brand,
            category: item.item_category,
            price: item.price,
          },
        ],
      },
    });
  }

  // ---------- 2) view_item_list (Collection/Search listing) ----------
  // -------- view_item_list by real viewport visibility --------
  const _seenImpressions = new Set();
  const _pendingByList = new Map();
  let _flushTimer = null;

  function normVariantTitle(t) {
    const s = (t || "").trim();
    if (!s) return "";
    if (s.toLowerCase() === "default title") return "";
    return s;
  }

  function buildItemFromEl(a) {
    const listName = (a.getAttribute("data-ec-list") || "").trim() || "Product list";
    const listId = (a.getAttribute("data-ec-list-id") || "").trim() || undefined;

    let category = (a.getAttribute("data-ec-category") || "").trim();
    // Fallback: если вдруг пусто, попробуем вытащить из имени списка "Каталог - X"
    if (!category) {
      const m = listName.match(/-\s*(.+)$/);
      if (m) category = m[1].trim();
    }

    const item = {
      item_id: String(a.getAttribute("data-ec-variant-id") || ""),
      item_name: a.getAttribute("data-ec-title") || "",
      item_brand: a.getAttribute("data-ec-vendor") || "",
      item_category: category || "",
      item_variant: normVariantTitle(a.getAttribute("data-ec-variant-title") || "") || undefined,
      item_list_name: listName,
      item_list_id: listId,
      index: toFloat(a.getAttribute("data-ec-index")) || undefined,
      price: toFloat(a.getAttribute("data-ec-price")) || undefined,
      quantity: 1
    };

    // Не передаём item_variant если он бессмысленный
    if (!item.item_variant) delete item.item_variant;

    return item;
  }

  function scheduleFlush() {
    if (_flushTimer) return;
    _flushTimer = setTimeout(() => {
      _flushTimer = null;

      for (const [listKey, pack] of _pendingByList.entries()) {
        const { listName, listId, items } = pack;
        if (!items.length) continue;

        gaPush("view_item_list", {
          item_list_name: listName,
          item_list_id: listId,
          items
        });

        // Yandex impressions
        yEcom({
          impressions: items.map((it) => ({
            id: it.item_id,
            name: it.item_name,
            brand: it.item_brand,
            category: it.item_category,
            list: it.item_list_name,
            position: it.index,
            price: it.price
          }))
        });

        _pendingByList.delete(listKey);
      }
    }, 300); // батчим по 300мс
  }

  function queueImpression(item) {
    const listName = item.item_list_name || "Product list";
    const listId = item.item_list_id || "";
    const listKey = `${listName}::${listId}`;

    if (!_pendingByList.has(listKey)) {
      _pendingByList.set(listKey, { listName, listId: item.item_list_id, items: [] });
    }
    _pendingByList.get(listKey).items.push(item);
    scheduleFlush();
  }

  // Observe product cards
  const io = new IntersectionObserver((entries) => {
    for (const ent of entries) {
      if (!ent.isIntersecting) continue;
      const a = ent.target;

      const item = buildItemFromEl(a);
      if (!item.item_id) continue;

      const key = `imp:${item.item_id}:${item.item_list_name || ""}:${item.index || ""}`;
      if (_seenImpressions.has(key)) continue;
      _seenImpressions.add(key);

      queueImpression(item);
    }
  }, {
    threshold: 0.5 // считаем просмотром, когда 50% карточки видно
  });

  // Start observing existing product links
  function observeAllProductLinks(root = document) {
    root.querySelectorAll('a[data-ec-variant-id].js-product-click').forEach((a) => io.observe(a));
  }
  observeAllProductLinks();

  // If карточки подгружаются/меняются (слайдер, infinite scroll) — подхватываем новые
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches && node.matches('a[data-ec-variant-id].js-product-click')) {
          io.observe(node);
        } else if (node.querySelectorAll) {
          observeAllProductLinks(node);
        }
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  // ---------- 3) select_item (Click from list) ----------
  document.addEventListener(
    "click",
    function (e) {
      const a = e.target.closest("a[data-ec-variant-id]");
      if (!a) return;

      const item = {
        item_id: String(a.getAttribute("data-ec-variant-id") || ""),
        item_name: a.getAttribute("data-ec-title") || "",
        item_brand: a.getAttribute("data-ec-vendor") || "",
        item_category: a.getAttribute("data-ec-category") || "",
        item_variant: a.getAttribute("data-ec-variant-title") || "",
        item_list_name: a.getAttribute("data-ec-list") || "",
        index: toFloat(a.getAttribute("data-ec-index")) || undefined,
        price: toFloat(a.getAttribute("data-ec-price")) || undefined,
        quantity: 1,
      };

      const key = `select_item:${item.item_id}:${item.item_list_name || ""}:${item.index || ""}`;
      if (!once(key, 1000)) return;

      // Save list context for further events (view_item/add_to_cart/checkout)
      saveListCtx(item);

      gaPush("select_item", {
        item_list_name: item.item_list_name || undefined,
        items: [item],
      });

      yEcom({
        click: {
          actionField: { list: item.item_list_name || "" },
          products: [
            {
              id: item.item_id,
              name: item.item_name,
              brand: item.item_brand,
              category: item.item_category,
              position: item.index,
              price: item.price,
            },
          ],
        },
      });
    },
    true
  );

  // ---------- Common add_to_cart handler ----------
  function handleAddToCartFromLineItem(data) {
    const price = toFloat((data.final_price || data.price || 0) / 100) || undefined;
    const qty = Number(data.quantity || 1);

    let item = {
      item_id: getItemId({ id: data.id, sku: data.sku }),
      item_name: data.product_title || data.title || "",
      item_brand: data.vendor || "",
      item_category: data.product_type || "",
      item_variant: data.variant_title || "",
      price,
      quantity: qty,
    };

    item = applyListCtx(item);

    const dedupeKey = `add_to_cart:${item.item_id}:${qty}:${price ?? ""}`;
    if (!once(dedupeKey, 1500)) return;

    gaPush("add_to_cart", {
      value: price ? price * qty : undefined,
      items: [item],
    });

    yEcom({
      add: {
        products: [
          {
            id: item.item_id,
            name: item.item_name,
            brand: item.item_brand,
            category: item.item_category,
            price: price,
            quantity: qty,
          },
        ],
      },
    });
  }

  // ---------- 4) add_to_cart via fetch intercept (/cart/add(.js)?) ----------
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const res = await originalFetch.apply(this, args);

    try {
      const url = getUrl(args[0]);
      if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
        const cloned = res.clone();
        const data = await cloned.json().catch(() => null);
        if (data && data.id) handleAddToCartFromLineItem(data);
      }

      // Cart mutations that return full cart JSON:
      if (/\/cart\/(change|update|clear)(\.js)?(\?|$)/.test(url)) {
        const cloned = res.clone();
        const cart = await cloned.json().catch(() => null);
        if (cart && cart.items) handleCartMutation(cart);
      }
    } catch (err) {
      // Silent fail
    }

    return res;
  };

  // ---------- 4b) add_to_cart & remove_from_cart via XHR intercept ----------
  (function patchXHR() {
    const open = XMLHttpRequest.prototype.open;
    const send = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__ec_url = url;
      return open.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (body) {
      this.addEventListener("load", function () {
        try {
          const url = String(this.__ec_url || "");
          const txt = this.responseText || "";
          if (!txt) return;

          if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
            const data = safeJsonParse(txt);
            if (data && data.id) handleAddToCartFromLineItem(data);
            return;
          }

          if (/\/cart\/(change|update|clear)(\.js)?(\?|$)/.test(url)) {
            const cart = safeJsonParse(txt);
            if (cart && cart.items) handleCartMutation(cart);
            return;
          }
        } catch (e) {}
      });

      return send.call(this, body);
    };
  })();

  // ---------- Cart mutation diff => add_to_cart/remove_from_cart (qty +/-) ----------
  let lastCart = null;

  function handleCartMutation(newCart) {
    if (!lastCart) {
      lastCart = newCart;
      return;
    }

    const changes = diffCart(lastCart, newCart);
    lastCart = newCart;

    for (const ch of changes) {
      const unit = ch.unit_price || undefined;
      const absQty = Math.abs(ch.delta_qty);

      // Build item (apply list ctx by item_id)
      let item = applyListCtx({
        item_id: getItemId({ variant_id: ch.variant_id, sku: ch.sku }),
        item_name: ch.product_title || ch.title || "",
        item_brand: ch.vendor || "",
        item_category: ch.product_type || "",
        item_variant: ch.variant_title || "",
        price: unit,
        quantity: absQty,
      });

      const value = unit ? unit * absQty : undefined;

      if (ch.delta_qty > 0) {
        // Quantity increased => add_to_cart
        const k = `add_to_cart:cart_change:${item.item_id}:${absQty}:${unit ?? ""}`;
        if (!once(k, 1500)) continue;

        gaPush("add_to_cart", { value, items: [item] });

        yEcom({
          add: {
            products: [
              {
                id: item.item_id,
                name: item.item_name,
                brand: item.item_brand,
                category: item.item_category,
                price: unit,
                quantity: absQty,
              },
            ],
          },
        });
      } else {
        // Quantity decreased => remove_from_cart
        const k = `remove_from_cart:${item.item_id}:${absQty}:${unit ?? ""}`;
        if (!once(k, 1500)) continue;

        gaPush("remove_from_cart", { value, items: [item] });

        yEcom({
          remove: {
            products: [
              {
                id: item.item_id,
                name: item.item_name,
                brand: item.item_brand,
                category: item.item_category,
                price: unit,
                quantity: absQty,
              },
            ],
          },
        });
      }
    }
  }

// ---------- 5) begin_checkout (Click to /checkout) ----------
  document.addEventListener("click", function (e) {
    const el = e.target.closest(
      'a[href^="/checkout"], button[name="checkout"], button[data-checkout], input[name="checkout"]'
    );
    if (!el) return;

    // ---- Terms & Conditions gate (must run BEFORE once()) ----
    const checkbox = document.getElementById("agree_terms_and_conditions");
    const errorEl = document.getElementById("terms_and_conditions_error");

    if (checkbox) {
      if (!checkbox.checked) {
        // Block checkout
        e.preventDefault();
        e.stopImmediatePropagation();
        if (errorEl) errorEl.style.display = "inline-block";
        checkbox.focus();
        return;
      } else {
        if (errorEl) errorEl.style.display = "none";
      }
    }

    // Dedupe only after validation passed
    if (!once("begin_checkout", 2000)) return;

    // We fully control navigation now
    e.preventDefault();
    e.stopImmediatePropagation();

    let proceeded = false;
    const proceedOnce = () => {
      if (proceeded) return;
      proceeded = true;

      // If it's a link to checkout, follow it; otherwise go to /checkout
      if (el.tagName === "A" && el.href) {
        window.location.href = el.href;
      } else {
        window.location.href = "/checkout";
      }
    };

    (async () => {
      try {
        const cart = await fetchCart();
        lastCart = cart;

        const items = (cart.items || []).map((it, idx) => buildGAItemFromCartLine(it, idx + 1));

        gaPushWithCallback(
          "begin_checkout",
          {
            value: toFloat((cart.total_price || 0) / 100) || undefined,
            items,
          },
          proceedOnce
        );

        yEcom({
          checkout: {
            actionField: { step: 1 },
            products: items.map((it) => ({
              id: it.item_id,
              name: it.item_name,
              brand: it.item_brand,
              category: it.item_category,
              price: it.price,
              quantity: it.quantity,
            })),
          },
        });

        // Safety fallback
        setTimeout(proceedOnce, 900);
      } catch (err) {
        proceedOnce();
      }
    })();
  }, true);

  // ---------- 6) view_cart (only on /cart page load) ----------
  (async function initCartSnapshotAndViewCart() {
    try {
      const cart = await fetchCart();
      lastCart = cart;

      if (location && typeof location.pathname === "string" && location.pathname.indexOf("/cart") === 0) {
        if (!once("view_cart", 3000)) return;

        const items = (cart.items || []).map((it, idx) => buildGAItemFromCartLine(it, idx + 1));

        gaPush("view_cart", {
          value: toFloat((cart.total_price || 0) / 100) || undefined,
          items,
        });

        yEcom({
          // Yandex does not have a strict "view_cart" analogue in Enhanced Ecommerce,
          // but sending cart products here is still useful if you want a custom layer.
          // If you prefer, you can remove this block.
          cart: {
            products: items.map((it) => ({
              id: it.item_id,
              name: it.item_name,
              brand: it.item_brand,
              category: it.item_category,
              price: it.price,
              quantity: it.quantity,
            })),
          },
        });
      }
    } catch (e) {}
  })();
})();
